<template>
  <div class="container">
    <div class="row mt-2">
      <div class="col-12">
        <!-- Breadcrumb Navigation -->
        <nav aria-label="breadcrumb" class="mb-4">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link to="/admin" class="text-decoration-none">
                <font-awesome-icon icon="cog" class="me-1" />
                Admin
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">
              <font-awesome-icon icon="ticket-alt" class="me-1" />
              Coupon-Verwaltung
            </li>
          </ol>
        </nav>

        <div class="d-flex justify-content-between align-items-center mb-4">
          <h1 class="h3 mb-0">Coupon-Verwaltung</h1>
          <div class="d-flex gap-2">
            <button @click="showCodes = !showCodes" class="btn btn-outline-secondary">
              <font-awesome-icon :icon="showCodes ? 'eye-slash' : 'eye'" class="me-1" />
              {{ showCodes ? 'Codes ausblenden' : 'Codes anzeigen' }}
            </button>
            <button @click="showCreateModal = true" class="btn btn-primary">
              <font-awesome-icon icon="plus" class="me-1" /> Neuer Coupon
            </button>
          </div>
        </div>

        <!-- Filter und Suche -->
        <div class="card mb-4">
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <label class="form-label">Status</label>
                <multiselect
                  v-model="filters.status"
                  :options="statusOptions"
                  :searchable="true"
                  :close-on-select="true"
                  :use-teleport="true"
                  :show-labels="false"
                  placeholder="Status auswählen..."
                  label="label"
                  track-by="value"
                  :no-options="'Keine Status verfügbar'"
                  :no-results="'Keine Status gefunden'"
                  @select="loadCoupons(1)"
                  @remove="loadCoupons(1)"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Typ</label>
                <multiselect
                  v-model="filters.type"
                  :options="typeOptions"
                  :searchable="true"
                  :close-on-select="true"
                  :use-teleport="true"
                  :show-labels="false"
                  placeholder="Typ auswählen..."
                  label="label"
                  track-by="value"
                  :no-options="'Keine Typen verfügbar'"
                  :no-results="'Keine Typen gefunden'"
                  @select="loadCoupons(1)"
                  @remove="loadCoupons(1)"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Anwendbar auf</label>
                <multiselect
                  v-model="filters.applicableTo"
                  :options="applicableToOptions"
                  :searchable="true"
                  :close-on-select="true"
                  :use-teleport="true"
                  :show-labels="false"
                  placeholder="Anwendbar auf..."
                  label="label"
                  track-by="value"
                  :no-options="'Keine Optionen verfügbar'"
                  :no-results="'Keine Optionen gefunden'"
                  @select="loadCoupons(1)"
                  @remove="loadCoupons(1)"
                />
              </div>
              <div class="col-md-3">
                <label class="form-label">Suche</label>
                <input 
                  v-model="filters.search" 
                  type="text" 
                  class="form-control" 
                  placeholder="Code oder Name..."
                  @input="debounceSearch"
                >
              </div>
              <div class="col-md-3">
                <label class="form-label">Coupon-Typ</label>
                <multiselect
                  v-model="filters.automatic"
                  :options="couponTypeOptions"
                  :searchable="true"
                  :close-on-select="true"
                  :use-teleport="true"
                  :show-labels="false"
                  placeholder="Coupon-Typ auswählen..."
                  label="label"
                  track-by="value"
                  :no-options="'Keine Typen verfügbar'"
                  :no-results="'Keine Typen gefunden'"
                  @select="loadCoupons(1)"
                  @remove="loadCoupons(1)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Coupons Tabelle -->
        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Laden...</span>
              </div>
            </div>

            <div v-else-if="coupons.length === 0" class="text-center py-4">
              <p class="text-muted">Keine Coupons gefunden</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Typ</th>
                    <th>Wert</th>
                    <th>Nutzung</th>
                    <th>Status</th>
                    <th>Ablaufdatum</th>
                    <th>Erstellt von</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="coupon in coupons" :key="coupon.id">
                    <td>
                      <code 
                        class="bg-primary text-white px-2 py-1 rounded coupon-code"
                        :class="{ 'blur-code': !showCodes }"
                        @click="copyToClipboard(coupon.code)"
                        :title="showCodes ? 'Klicken zum Kopieren' : 'Codes anzeigen um zu kopieren'"
                        style="cursor: pointer;"
                      >
                        {{ showCodes ? coupon.code : '••••••••' }}
                      </code>
                    </td>
                    <td>{{ coupon.name }}</td>
                    <td>
                      <span class="badge" :class="getTypeBadgeClass(coupon.type)">
                        {{ getTypeLabel(coupon.type) }}
                      </span>
                    </td>
                    <td>
                      <span v-if="coupon.type === 'percentage_discount'">{{ coupon.value }}%</span>
                      <span v-else-if="coupon.type === 'free_coins'">{{ Math.round(coupon.value) }} Quittungen</span>
                      <span v-else-if="coupon.type === 'free_team_pro'">
                        {{ coupon.teamProPlan === 'ultimate' ? 'Ultimate' : 'Pro' }} 
                        <span v-if="coupon.teamProDuration">({{ coupon.teamProDuration }} {{ getDurationUnitLabel(coupon.teamProDurationUnit) }})</span> 
                        <span v-if="coupon.teamProCoins > 0" class="text-success">+ {{ coupon.teamProCoins }} Quittungen</span>
                      </span>
                    </td>
                    <td>
                      <span v-if="coupon.maxUsage === 0">
                        {{ coupon.currentUsage }} (unbegrenzt)
                        <div class="progress mt-1" style="height: 4px;">
                          <div class="progress-bar bg-success" style="width: 100%"></div>
                        </div>
                      </span>
                      <span v-else>
                        {{ coupon.currentUsage }} / {{ coupon.maxUsage }}
                        <div class="progress mt-1" style="height: 4px;">
                          <div 
                            class="progress-bar" 
                            :class="getUsageProgressClass(coupon.currentUsage, coupon.maxUsage)"
                            :style="{ width: getUsagePercentage(coupon.currentUsage, coupon.maxUsage) + '%' }"
                          ></div>
                        </div>
                      </span>
                    </td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(coupon)">
                        {{ getStatusLabel(coupon) }}
                      </span>
                    </td>
                    <td>
                      <span v-if="coupon.expiresAt">
                        {{ formatDate(coupon.expiresAt) }}
                      </span>
                      <span v-else class="text-muted">Kein Ablaufdatum</span>
                    </td>
                    <td>
                      <span v-if="coupon.User">
                        {{ coupon.User.firstName }} {{ coupon.User.lastName }}
                      </span>
                      <span v-else class="text-muted">Unbekannt</span>
                    </td>
          
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button 
                          @click="editCoupon(coupon)" 
                          class="btn btn-outline-primary"
                          title="Bearbeiten"
                        >
                          <font-awesome-icon icon="edit" />
                        </button>
                        <button 
                          @click="viewUsages(coupon)" 
                          class="btn btn-outline-info"
                          title="Nutzungen anzeigen"
                        >
                          <font-awesome-icon icon="eye" />
                        </button>
                        <button 
                          @click="handleDeleteCoupon(coupon)" 
                          class="btn btn-outline-danger"
                          title="Löschen"
                          :disabled="coupon.currentUsage > 0"
                        >
                          <font-awesome-icon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="pagination.totalPages > 1" class="d-flex justify-content-center mt-4">
              <nav>
                <ul class="pagination">
                  <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
                    <button class="page-link" @click="loadCoupons(pagination.page - 1)">Zurück</button>
                  </li>
                  <li 
                    v-for="page in getPageNumbers()" 
                    :key="page" 
                    class="page-item"
                    :class="{ active: page === pagination.page }"
                  >
                    <button class="page-link" @click="loadCoupons(page)">{{ page }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: pagination.page >= pagination.totalPages }">
                    <button class="page-link" @click="loadCoupons(pagination.page + 1)">Weiter</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal" :class="{ show: showCreateModal || showEditModal }" :style="{ display: (showCreateModal || showEditModal) ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? 'Coupon bearbeiten' : 'Neuer Coupon' }}</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveCoupon">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Code *</label>
                    <input 
                      v-model="form.code" 
                      type="text" 
                      class="form-control" 
                      required
                      :disabled="isEditing"
                      placeholder="z.B. SAVE20"
                    >
                    <div class="form-text">Eindeutiger Coupon-Code (wird automatisch in Großbuchstaben konvertiert)</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Name *</label>
                    <input 
                      v-model="form.name" 
                      type="text" 
                      class="form-control" 
                      required
                      placeholder="z.B. 20% Rabatt"
                    >
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Beschreibung</label>
                <textarea 
                  v-model="form.description" 
                  class="form-control" 
                  rows="3"
                  placeholder="Optionale Beschreibung des Coupons"
                ></textarea>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Typ *</label>
                    <select v-model="form.type" class="form-select" required>
                      <option value="">Typ auswählen</option>
                      <option value="free_coins">Kostenlose Quittungen</option>
                      <option value="percentage_discount">Prozentualer Rabatt</option>
                      <option value="free_team_pro">Kostenloses Pro-Paket</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Wert *</label>
                    <input 
                      v-model.number="form.value" 
                      type="number" 
                      class="form-control" 
                      required
                      :min="0"
                      :max="form.type === 'percentage_discount' ? 100 : 999999"
                      :step="form.type === 'free_coins' ? 1 : (form.type === 'percentage_discount' ? 1 : 0.01)"
                      :placeholder="form.type === 'percentage_discount' ? '20' : '50'"
                    >
                    <div class="form-text">
                      <span v-if="form.type === 'percentage_discount'">Prozentsatz (0-100)</span>
                      <span v-else-if="form.type === 'free_coins'">Anzahl kostenloser Quittungen</span>
                      <span v-else-if="form.type === 'free_team_pro'">Nicht relevant</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Max. Nutzungen</label>
                    <input 
                      v-model.number="form.maxUsage" 
                      type="number" 
                      class="form-control" 
                      :min="0"
                      placeholder="1"
                    >
                    <div class="form-text">
                      <span v-if="form.maxUsage === 0">0 = Unbegrenzt</span>
                      <span v-else>Maximale Anzahl der Nutzungen</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Ablaufdatum</label>
                    <input 
                      v-model="form.expiresAt" 
                      type="datetime-local" 
                      class="form-control"
                    >
                    <div class="form-text">Optional - leer lassen für kein Ablaufdatum</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Anwendbar auf</label>
                    <select v-model="form.applicableTo" class="form-select">
                      <option value="both">Beide (Quittung-Kauf & Team-Upgrade)</option>
                      <option value="coin_purchase">Nur Quittung-Kauf</option>
                      <option value="team_upgrade">Nur Team-Upgrade</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Mindestbestellwert</label>
                    <input 
                      v-model.number="form.minPurchaseAmount" 
                      type="number" 
                      class="form-control" 
                      :min="0"
                      :step="0.01"
                      placeholder="0.00"
                    >
                    <div class="form-text">Optional - Mindestbetrag für Coupon-Gültigkeit</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Max. Rabattbetrag</label>
                    <input 
                      v-model.number="form.maxDiscountAmount" 
                      type="number" 
                      class="form-control" 
                      :min="0"
                      :step="0.01"
                      placeholder="0.00"
                    >
                    <div class="form-text">Optional - Maximaler Rabattbetrag (nur bei %-Rabatt)</div>
                  </div>
                </div>
              </div>

              <!-- Pro-Paket Felder (nur bei free_team_pro) -->
              <div v-if="form.type === 'free_team_pro'" class="row">
                <div class="col-md-3">
                  <div class="mb-3">
                    <label class="form-label">Laufzeit *</label>
                    <input 
                      v-model.number="form.teamProDuration" 
                      type="number" 
                      class="form-control" 
                      :min="1"
                      :max="365"
                      required
                    >
                    <div class="form-text">Laufzeit des kostenlosen Pro-Paket</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="mb-3">
                    <label class="form-label">Einheit *</label>
                    <select v-model="form.teamProDurationUnit" class="form-select" required>
                      <option value="days">Tage</option>
                      <option value="months">Monate</option>
                      <option value="years">Jahre</option>
                    </select>
                    <div class="form-text">Zeiteinheit der Laufzeit</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="mb-3">
                    <label class="form-label">Inklusive Quittungen</label>
                    <input 
                      v-model.number="form.teamProCoins" 
                      type="number" 
                      class="form-control" 
                      :min="0"
                      placeholder="0"
                    >
                    <div class="form-text">Gesamtanzahl der Quittungen (nicht pro Monat)</div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="mb-3">
                    <label class="form-label">Team Plan *</label>
                    <select v-model="form.teamProPlan" class="form-select" required>
                      <option value="pro">Pro</option>
                      <option value="ultimate">Ultimate</option>
                    </select>
                    <div class="form-text">Art des kostenlosen Team Plans</div>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input 
                        v-model="form.isActive" 
                        type="checkbox" 
                        class="form-check-input" 
                        id="isActive"
                      >
                      <label class="form-check-label" for="isActive">
                        Coupon ist aktiv
                      </label>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input 
                        v-model="form.multiUsage" 
                        type="checkbox" 
                        class="form-check-input" 
                        id="multiUsage"
                      >
                      <label class="form-check-label" for="multiUsage">
                        Multi-Usage (unbegrenzte Nutzungen)
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Abbrechen</button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveCoupon"
              :disabled="saving"
            >
              <div v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></div>
              {{ isEditing ? 'Aktualisieren' : 'Erstellen' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Usages Modal -->
    <div class="modal" :class="{ show: showUsagesModal }" :style="{ display: showUsagesModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Coupon-Nutzungen: {{ selectedCoupon?.code }}</h5>
            <button type="button" class="btn-close" @click="showUsagesModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="usagesLoading" class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Laden...</span>
              </div>
            </div>

            <div v-else-if="usages.length === 0" class="text-center py-4">
              <p class="text-muted">Keine Nutzungen gefunden</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Benutzer</th>
                    <th>Team</th>
                    <th>Referenz</th>
                    <th>Originalbetrag</th>
                    <th>Rabatt</th>
                    <th>Endbetrag</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="usage in usages" :key="usage.id">
                    <td>{{ formatDate(usage.usedAt) }}</td>
                    <td>
                      <span v-if="usage.User">
                        {{ usage.User.firstName }} {{ usage.User.lastName }}
                        <br><small class="text-muted">{{ usage.User.email }}</small>
                      </span>
                      <span v-else class="text-muted">Unbekannt</span>
                    </td>
                    <td>
                      <span v-if="usage.Team">{{ usage.Team.name }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span class="badge" :class="getReferenceBadgeClass(usage.referenceType)">
                        {{ getReferenceLabel(usage.referenceType) }}
                      </span>
                    </td>
                    <td>{{ formatCurrency(usage.originalAmount) }}</td>
                    <td class="text-success">{{ Number(usage.discountAmount) > 0 ? '-' + formatCurrency(usage.discountAmount) : formatCurrency(usage.discountAmount) }}</td>
                    <td>{{ formatCurrency(usage.finalAmount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div v-if="showCreateModal || showEditModal || showUsagesModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { couponsApi } from '@/api/coupons'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import Multiselect from 'vue-multiselect'


const loading = ref(false)
const saving = ref(false)
const usagesLoading = ref(false)
const coupons = ref([])
const usages = ref([])
const showCodes = ref(false) 
const pagination = ref({
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0
})


const showCreateModal = ref(false)
const showEditModal = ref(false)
const showUsagesModal = ref(false)
const selectedCoupon = ref(null)


const filters = ref({
  status: null,
  type: null,
  applicableTo: null,
  search: '',
  automatic: null
})

// Options for multiselects
const statusOptions = [
  { value: '', label: 'Alle' },
  { value: 'active', label: 'Aktiv' },
  { value: 'inactive', label: 'Inaktiv' },
  { value: 'expired', label: 'Abgelaufen' }
]

const typeOptions = [
  { value: '', label: 'Alle' },
  { value: 'free_coins', label: 'Kostenlose Quittungen' },
  { value: 'percentage_discount', label: 'Prozentualer Rabatt' },
  { value: 'free_team_pro', label: 'Kostenloses Pro-Paket' }
]

const applicableToOptions = [
  { value: '', label: 'Alle' },
  { value: 'coin_purchase', label: 'Quittung-Kauf' },
  { value: 'team_upgrade', label: 'Team-Upgrade' },
  { value: 'both', label: 'Beide' }
]

const couponTypeOptions = [
  { value: 'false', label: 'Manuelle Coupons' },
  { value: 'true', label: 'Automatische Coupons' },
  { value: '', label: 'Alle Coupons' }
]


const form = ref({
  code: '',
  name: '',
  description: '',
  type: '',
  value: 0,
  maxUsage: 1,
  multiUsage: false,
  expiresAt: '',
  isActive: true,
  applicableTo: 'both',
  minPurchaseAmount: null,
  maxDiscountAmount: null
})


const isEditing = computed(() => showEditModal.value)


const loadCoupons = async (page = 1) => {
  try {
    loading.value = true
    const params = {
      page,
      limit: pagination.value.limit,
      ...(filters.value.status && filters.value.status.value && { status: filters.value.status.value }),
      ...(filters.value.type && filters.value.type.value && { type: filters.value.type.value }),
      ...(filters.value.applicableTo && filters.value.applicableTo.value && { applicableTo: filters.value.applicableTo.value }),
      ...(filters.value.search && { search: filters.value.search }),
      ...(filters.value.automatic && filters.value.automatic.value && { automatic: filters.value.automatic.value })
    }
    const result = await couponsApi.getAllCoupons(params)
    coupons.value = result.coupons
    pagination.value = result.pagination
  } catch (error) {
    console.error('Error loading coupons:', error)
    showError('Fehler beim Laden der Coupons')
  } finally {
    loading.value = false
  }
}

const debounceSearch = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      loadCoupons(1)
    }, 300)
  }
})()

const resetForm = () => {
  form.value = {
    code: '',
    name: '',
    description: '',
    type: '',
    value: 0,
    maxUsage: 1,
    multiUsage: false,
    expiresAt: '',
    isActive: true,
    applicableTo: 'both',
    minPurchaseAmount: null,
    maxDiscountAmount: null,
    teamProDuration: '',
    teamProDurationUnit: 'months',
    teamProCoins: 0,
    teamProPlan: 'pro'
  }
}

const editCoupon = (coupon) => {
  selectedCoupon.value = coupon
  form.value = {
    code: coupon.code,
    name: coupon.name,
    description: coupon.description || '',
    type: coupon.type,
    value: parseFloat(coupon.value),
    maxUsage: coupon.maxUsage,
    multiUsage: coupon.multiUsage || false,
    expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().slice(0, 16) : '',
    isActive: coupon.isActive,
    applicableTo: coupon.applicableTo,
    minPurchaseAmount: coupon.minPurchaseAmount ? parseFloat(coupon.minPurchaseAmount) : null,
    maxDiscountAmount: coupon.maxDiscountAmount ? parseFloat(coupon.maxDiscountAmount) : null,
    teamProDuration: coupon.teamProDuration || '',
    teamProDurationUnit: coupon.teamProDurationUnit || 'months',
    teamProCoins: coupon.teamProCoins || 0,
    teamProPlan: coupon.teamProPlan || 'pro'
  }
  showEditModal.value = true
}

const viewUsages = async (coupon) => {
  try {
    usagesLoading.value = true
    selectedCoupon.value = coupon
    const result = await couponsApi.getCouponUsages(coupon.id)
    usages.value = result.usages
    showUsagesModal.value = true
  } catch (error) {
    console.error('Error loading usages:', error)
    showError('Fehler beim Laden der Nutzungen')
  } finally {
    usagesLoading.value = false
  }
}

const handleDeleteCoupon = async (coupon) => {
  if (coupon.currentUsage > 0) {
    showError('Coupon kann nicht gelöscht werden, da er bereits verwendet wurde')
    return
  }

  const confirmed = await showConfirmDialog(
    'Coupon löschen',
    `Möchtest du den Coupon "${coupon.code}" wirklich löschen?`,
    'Löschen',
    'Abbrechen'
  )

  if (confirmed.isConfirmed) {
    try {
      await couponsApi.deleteCoupon(coupon.id)
      showSuccess('Coupon erfolgreich gelöscht')
      loadCoupons(pagination.value.page)
    } catch (error) {
      console.error('Error deleting coupon:', error)
      showError('Fehler beim Löschen des Coupons')
    }
  }
}

const saveCoupon = async () => {
  try {
    saving.value = true
    
    
    let formattedValue;
    if (form.value.type === 'free_coins') {
      formattedValue = parseInt(form.value.value);
    } else {
      formattedValue = parseFloat(form.value.value);
    }

    const couponData = {
      ...form.value,
      value: formattedValue,
      maxUsage: parseInt(form.value.maxUsage),
      multiUsage: form.value.multiUsage,
      expiresAt: form.value.expiresAt ? new Date(form.value.expiresAt).toISOString() : null,
      minPurchaseAmount: form.value.minPurchaseAmount ? parseFloat(form.value.minPurchaseAmount) : null,
      maxDiscountAmount: form.value.maxDiscountAmount ? parseFloat(form.value.maxDiscountAmount) : null,
      teamProDuration: form.value.type === 'free_team_pro' ? parseInt(form.value.teamProDuration) : null,
      teamProDurationUnit: form.value.type === 'free_team_pro' ? form.value.teamProDurationUnit : null,
      teamProCoins: form.value.type === 'free_team_pro' ? parseInt(form.value.teamProCoins) : null,
      teamProPlan: form.value.type === 'free_team_pro' ? form.value.teamProPlan : null
    }

    if (isEditing.value) {
      await couponsApi.updateCoupon(selectedCoupon.value.id, couponData)
      showSuccess('Coupon erfolgreich aktualisiert')
    } else {
      await couponsApi.createCoupon(couponData)
      showSuccess('Coupon erfolgreich erstellt')
    }

    closeModal()
    loadCoupons(pagination.value.page)
  } catch (error) {
    console.error('Error saving coupon:', error)
    const backendMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message

    showError(backendMessage || 'Fehler beim Speichern des Coupons')
  } finally {
    saving.value = false
  }
}

const closeModal = () => {
  showCreateModal.value = false
  showEditModal.value = false
  showUsagesModal.value = false
  selectedCoupon.value = null
  resetForm()
}

const getTypeLabel = (type) => {
  const labels = {
    free_coins: 'Kostenlose Quittungen',
    percentage_discount: 'Prozentualer Rabatt',
    free_team_pro: 'Kostenloses Pro-Paket'
  }
  return labels[type] || type
}

const getTypeBadgeClass = (type) => {
  const classes = {
    free_coins: 'bg-primary',
    percentage_discount: 'bg-success',
    free_team_pro: 'bg-warning'
  }
  return classes[type] || 'bg-secondary'
}

const getStatusLabel = (coupon) => {
  if (!coupon.isActive) return 'Inaktiv'
  if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) return 'Abgelaufen'
  if (coupon.currentUsage >= coupon.maxUsage) return 'Verbraucht'
  return 'Aktiv'
}

const getStatusBadgeClass = (coupon) => {
  if (!coupon.isActive) return 'bg-secondary'
  if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) return 'bg-danger'
  if (coupon.currentUsage >= coupon.maxUsage) return 'bg-warning'
  return 'bg-success'
}

const getUsagePercentage = (current, max) => {
  return Math.min((current / max) * 100, 100)
}

const getUsageProgressClass = (current, max) => {
  const percentage = (current / max) * 100
  if (percentage >= 90) return 'bg-danger'
  if (percentage >= 75) return 'bg-warning'
  return 'bg-success'
}

const getReferenceLabel = (type) => {
  const labels = {
    billing: 'Quittung-Kauf',
    teamBilling: 'Team-Upgrade'
  }
  return labels[type] || type
}

const getDurationUnitLabel = (unit) => {
  const labels = {
    days: 'Tage',
    months: 'Monate',
    years: 'Jahre'
  }
  return labels[unit] || unit
}

const getReferenceBadgeClass = (type) => {
  const classes = {
    billing: 'bg-primary',
    teamBilling: 'bg-info'
  }
  return classes[type] || 'bg-secondary'
}

const getPageNumbers = () => {
  const pages = []
  const start = Math.max(1, pagination.value.page - 2)
  const end = Math.min(pagination.value.totalPages, pagination.value.page + 2)
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatCurrency = (amount) => {
  if (Number(amount) === 0 || amount === null || amount === undefined) {
    return ''
  }
  return new Intl.NumberFormat('de-DE', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(amount)
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    showSuccess('Coupon-Code in die Zwischenablage kopiert!')
  } catch (error) {
    console.error('Fehler beim Kopieren:', error)
    
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    try {
      document.execCommand('copy')
      showSuccess('Coupon-Code in die Zwischenablage kopiert!')
    } catch (fallbackError) {
      console.error('Fallback kopieren fehlgeschlagen:', fallbackError)
      showError('Fehler beim Kopieren des Codes')
    }
    document.body.removeChild(textArea)
  }
}


onMounted(() => {
  loadCoupons()
})
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
